/** @file TileTabStrip: the open Tile tabs in the top bar (label, unsaved dot, ×; middle click closes). */
import { useEffect, useRef } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { useStore } from 'zustand';
import { sameTile } from '../../state/sameTile';
import type { TileRef } from '../../state/editorTypes';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { tileDraftKey } from './tileDraftKey';
import { tileDrafts } from './tileDrafts';
import { tileRequestClose } from './tileRequestClose';
import { tileTabLabel } from './tileTabLabel';

/** The puppet the tabs belong to and the tab the route shows (null on Pose / Art / Agent). */
export type TileTabStripProps = { puppetId: string; active: TileRef | null };

/** One tab per `shell.tiles` entry, in order, after Pose / Art / Agent; the shown tab scrolls into view. */
export function TileTabStrip({ puppetId, active }: TileTabStripProps): ReactElement | null {
  const store = useEditorStore();
  const tiles = useEditor((s) => s.shell.tiles);
  const puppet = useEditor((s) => s.puppet.doc?.puppet ?? null);
  const dirty = useStore(tileDrafts, (s) => s.drafts);
  const strip = useRef<HTMLDivElement>(null);
  const shown = active === null ? null : tileDraftKey(puppetId, active);
  useEffect(() => {
    const tab = shown === null ? null : strip.current?.querySelector(`[data-tile="${CSS.escape(shown)}"]`);
    try { tab?.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch { /* no layout here (jsdom) */ }
  }, [shown]);
  if (tiles.length === 0) return null;
  const close = (ref: TileRef) => (e: MouseEvent): void => { e.stopPropagation(); tileRequestClose(store, ref); };
  return (
    <div ref={strip} className="p3-tabs p3-tile-tabs" role="tablist" aria-label="Tile tabs">
      {tiles.map((t) => {
        const key = tileDraftKey(puppetId, t);
        const d = dirty[key];
        const unsaved = d !== undefined && d.version !== d.savedVersion;
        const label = tileTabLabel(puppet, t);
        return (
          <div key={key} role="tab" tabIndex={0} className="p3-tab p3-tile-tab" aria-selected={active !== null && sameTile(active, t)}
            title={`${t.drawable}/${String(t.entry)}/${String(t.tile)}${unsaved ? ' (unsaved strokes)' : ''}`} data-tile={key}
            onClick={() => { store.getState().openTile(t); }}
            onMouseDown={(e) => { if (e.button === 1) e.preventDefault(); }}
            onAuxClick={(e) => { if (e.button === 1) close(t)(e); }}>
            <span>{label}</span>
            {unsaved ? <span className="p3-tile-dot" aria-label="unsaved" /> : null}
            <button type="button" className="p3-tile-close" aria-label={`Close ${label}`} onClick={close(t)}>×</button>
          </div>
        );
      })}
    </div>
  );
}
